import { Fragment, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Card, Divider, IconButton, IconName, Stack, Tab, Tabs, Typography, Image } from '@optimus-web/core';
import { FormInputField, FormTextareaField, lt } from '@mews/framework';
import { ContentSection, Select } from '@mews-commander/ui';
import { DropzoneField } from '@mews-commander/form';
import { Option } from '@mews-commander/types';
import { useImageSrc } from '@mews-commander/utils';
import { useProductLanguages } from '../../../hooks/useProductLanguages';
import { useEnterprisesProductContext } from '../../../context/EnterprisesProductContext';
import { useGetCentralProductDetail } from '../../../queries/CentralProductsTable';
import { DetailsFieldName, UpdateDetailsConfigurationFormData } from '../../forms/schemas/configureProductFormSchema';

interface Props {
    isEditForm?: boolean;
    imageId?: string | null;
}

export const DetailsForm = ({ isEditForm, imageId }: Props) => {
    const {
        state: {
            centralProduct: { id },
        },
    } = useEnterprisesProductContext();
    const { data: { externalName, description } = {} } = useGetCentralProductDetail(id);
    const { activeTab, languagesList, portfolioDefaultLanguageCode, toggleSelection, selectedOptions, setActiveTab } =
        useProductLanguages();

    const { trigger, setValue } = useFormContext<UpdateDetailsConfigurationFormData>();
    const validateFilesSize = async () => {
        await trigger(DetailsFieldName.Image);
    };

    const [hideImage, setHideImage] = useState(false);

    const productImgSrc = useImageSrc(imageId);

    const handleImageDelete = () => {
        setValue(DetailsFieldName.ImageId, null);
        setHideImage(true);
    };

    return (
        <Stack spacing="300" vertical>
            <Stack spacing="200" vertical>
                <Typography textStyle="titleSmallStrong" as="div">
                    {lt('ConfigureProductSubHeadingLanguages')}
                </Typography>
                <Typography textStyle="bodyMedium" variant="secondary" as="div">
                    {lt('ConfigureProductLanguageBody')}
                </Typography>
            </Stack>
            <Select
                label={lt('ConfigureProductSubHeadingLanguages')}
                options={languagesList as unknown as Array<Option>}
                selected={selectedOptions?.map(({ value }) => value)}
                onChange={toggleSelection}
                multiselect
            />
            <Tabs
                activeTabValue={activeTab}
                inline
                onTabClick={(e, value) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveTab(value);
                }}
                showDivider
            >
                {(selectedOptions || []).map(({ value, label, isDefault }) => (
                    <Tab
                        icon={isDefault ? IconName.Stars : undefined}
                        id={value}
                        key={value}
                        label={label}
                        value={value}
                    />
                ))}
            </Tabs>
            {(selectedOptions || []).map(
                ({ value }) =>
                    activeTab === value && (
                        <Fragment key={value}>
                            <FormInputField
                                defaultValue={value === portfolioDefaultLanguageCode ? externalName || '' : ''}
                                name={`${DetailsFieldName.Languages}.${value}.${DetailsFieldName.ExternalName}`}
                                label={lt('ExternalName')}
                            />
                            <FormTextareaField
                                defaultValue={value === portfolioDefaultLanguageCode ? description : ''}
                                name={`${DetailsFieldName.Languages}.${value}.${DetailsFieldName.Description}`}
                                label={lt('ConfigureProductLanguageDescription')}
                                rows={4}
                            />
                        </Fragment>
                    )
            )}
            <Divider />
            <Stack spacing="200" vertical>
                <Typography textStyle="titleSmallStrong" as="div">
                    {lt('ConfigureProductSubHeadingProductImage')}
                </Typography>
                <Typography textStyle="bodyMedium" variant="secondary" as="div">
                    {lt('ConfigureProductDescriptionProductImage')}
                </Typography>
            </Stack>
            {isEditForm && productImgSrc && !hideImage ? (
                <Stack vertical spacing="50">
                    <Typography textStyle="bodyMediumStrong" as="div">
                        {lt('Image')}
                    </Typography>
                    <Card padding="100" color="black">
                        <Stack itemAlignment="center" distribution="space-between">
                            <Image height={48} width={86} rounded src={productImgSrc} objectFit="cover" />
                            <IconButton
                                variant="ghost"
                                icon={IconName.Delete}
                                type="button"
                                onClick={handleImageDelete}
                            />
                        </Stack>
                    </Card>
                </Stack>
            ) : (
                <DropzoneField
                    label={lt('ConfigureProductLanguageImage')}
                    name={DetailsFieldName.Image}
                    accept=".jpg, .jpeg, .png"
                    onChange={validateFilesSize}
                />
            )}
            {isEditForm && (
                <>
                    <ContentSection title={lt('Mapping')} spacing="300">
                        <FormInputField
                            name={DetailsFieldName.ExternalIdentifier}
                            label={lt('ExternalIdentifier')}
                            size="medium"
                            helper={lt('EnterpriseProductEditDetailsExternalIdenitifierHint')}
                        />
                    </ContentSection>
                </>
            )}
        </Stack>
    );
};
